import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Search, Calendar, X, RefreshCw } from 'lucide-react';
import { KPICard, WidgetCard, Pill, PrimaryButton } from '@/components/ui/native';

// Action chips — match Pill tones in /components/ui/native.js
const ACTION_TONE = {
  update: 'blue',
  reassign: 'purple',
  stage_change: 'amber',
  delete: 'rose',
  bulk_delete: 'rose',
  reopen: 'emerald',
};
const COLLECTION_ICON = {
  leads: '👥',
  opportunities: '💼',
  contacts: '👤',
  companies: '🏢',
};

function DiffPanel({ before, after }) {
  if (!before && !after) return <div className="text-xs text-slate-400">No diff captured</div>;
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (keys.length === 0) return <div className="text-xs text-slate-400">No fields changed</div>;
  return (
    <div className="space-y-1.5">
      {keys.map((k) => {
        const b = before?.[k];
        const a = after?.[k];
        return (
          <div key={k} className="font-mono text-xs grid grid-cols-12 gap-2">
            <span className="col-span-3 text-slate-500 truncate" title={k}>{k}</span>
            <span className="col-span-4 text-rose-600 dark:text-rose-400 truncate" title={JSON.stringify(b)}>
              {b === undefined ? <em className="text-slate-300">—</em> : JSON.stringify(b).slice(0, 60)}
            </span>
            <span className="col-span-1 text-slate-400 text-center">→</span>
            <span className="col-span-4 text-emerald-600 dark:text-emerald-400 truncate" title={JSON.stringify(a)}>
              {a === undefined ? <em className="text-slate-300">—</em> : JSON.stringify(a).slice(0, 60)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ action: '', collection: '', actor_id: '', days: 30 });
  const [expanded, setExpanded] = useState(null);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.action) params.action = filters.action;
      if (filters.collection) params.collection_name = filters.collection;
      if (filters.actor_id) params.actor_id = filters.actor_id;
      if (filters.days) {
        const since = new Date(Date.now() - filters.days * 24 * 60 * 60 * 1000).toISOString();
        params.start = since;
      }
      const res = await axios.get(`${API}/admin/audit`, { params });
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
      setHasMore(!!res.data?.has_more);
    } catch (e) {
      console.error('audit fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit/stats`, { params: { days: filters.days } });
      setStats(res.data);
    } catch (e) {
      console.error('audit stats fetch failed', e);
    }
  }, [filters.days]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Audit Log</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Track every sensitive mutation across leads, opportunities, contacts, and companies.
          </p>
        </div>
        <PrimaryButton
          onClick={() => { fetchLogs(); fetchStats(); }}
          icon={<RefreshCw className="w-4 h-4" />}
          data-testid="audit-refresh-btn"
        >
          Refresh
        </PrimaryButton>
      </div>

      {/* Stats — KPICard for native look */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="audit-stats-row">
          <KPICard title={`Events (${filters.days}d)`} value={stats.total} unit="all mutations" />
          <KPICard title="Distinct actions" value={stats.by_action.length} unit="action types" />
          <KPICard title="Collections" value={stats.by_collection.length} unit="affected" />
          <KPICard title="Active users" value={stats.top_actors.length} unit="distinct actors" />
        </div>
      )}

      {/* Filters */}
      <WidgetCard title="Filters" headerRight={<></>}>
        <div className="flex items-center gap-3 flex-wrap" data-testid="audit-filters">
          <select
            value={filters.action}
            onChange={(e) => { setFilters((f) => ({ ...f, action: e.target.value })); setPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            <option value="update">Update</option>
            <option value="reassign">Reassign</option>
            <option value="stage_change">Stage change</option>
            <option value="delete">Delete</option>
            <option value="bulk_delete">Bulk delete</option>
            <option value="reopen">Reopen</option>
          </select>
          <select
            value={filters.collection}
            onChange={(e) => { setFilters((f) => ({ ...f, collection: e.target.value })); setPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
            data-testid="audit-filter-collection"
          >
            <option value="">All collections</option>
            <option value="leads">Leads</option>
            <option value="opportunities">Opportunities</option>
            <option value="contacts">Contacts</option>
            <option value="companies">Companies</option>
          </select>
          <select
            value={filters.days}
            onChange={(e) => { setFilters((f) => ({ ...f, days: Number(e.target.value) })); setPage(1); }}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
            data-testid="audit-filter-days"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by actor user ID…"
              value={filters.actor_id}
              onChange={(e) => { setFilters((f) => ({ ...f, actor_id: e.target.value })); setPage(1); }}
              className="w-64 pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
              data-testid="audit-filter-actor"
            />
          </div>
          {(filters.action || filters.collection || filters.actor_id) && (
            <button
              onClick={() => setFilters({ action: '', collection: '', actor_id: '', days: filters.days })}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </WidgetCard>

      {/* Table */}
      <WidgetCard
        title={`Events ${loading ? '· loading…' : `· ${total}`}`}
        headerRight={hasMore ? <span className="text-xs text-slate-400 normal-case">Page {page}</span> : <></>}
      >
        {logs.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No audit events match these filters.</p>
            <p className="text-xs mt-1">Mutations on leads / opportunities / contacts / companies will appear here.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Time</th>
                <th className="text-left px-4 py-2 font-semibold">Actor</th>
                <th className="text-left px-4 py-2 font-semibold">Action</th>
                <th className="text-left px-4 py-2 font-semibold">Collection</th>
                <th className="text-left px-4 py-2 font-semibold">Doc / Meta</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((row) => {
                const isOpen = expanded === row.id;
                const meta = row.meta || {};
                const metaSummary = meta.name || meta.organization || meta.count
                  ? [meta.name, meta.organization, meta.count != null ? `${meta.count} record(s)` : null].filter(Boolean).join(' · ')
                  : (row.doc_id ? row.doc_id.slice(0, 8) + '…' : '—');
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                      className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer"
                      data-testid={`audit-row-${row.id}`}
                    >
                      <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmtTime(row.ts)}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-800 dark:text-slate-200 text-sm">{row.actor_name || '—'}</div>
                        <div className="text-xs text-slate-400">{row.actor_role || ''}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Pill tone={ACTION_TONE[row.action] || 'slate'}>{row.action}</Pill>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">
                        <span className="mr-1">{COLLECTION_ICON[row.collection] || '📄'}</span>
                        {row.collection}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400 truncate max-w-[280px]" title={metaSummary}>
                        {metaSummary}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="text-xs text-slate-400">{isOpen ? '▴' : '▾'}</span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700">
                        <td colSpan={6} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Diff</div>
                              <DiffPanel before={row.before} after={row.after} />
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Doc ID</div>
                              <code className="text-xs text-slate-700 dark:text-slate-300 break-all">{row.doc_id || '—'}</code>
                            </div>
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Meta</div>
                              <pre className="text-xs text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 rounded p-2 overflow-x-auto">{JSON.stringify(meta, null, 2)}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Pagination */}
        {(page > 1 || hasMore) && logs.length > 0 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-prev-btn"
            >
              Previous
            </button>
            <span className="text-xs text-slate-500">
              Page {page} • Showing {(page - 1) * limit + 1}–{(page - 1) * limit + logs.length} of {total}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-next-btn"
            >
              Next
            </button>
          </div>
        )}
      </WidgetCard>
    </div>
  );
}
